import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { LoadingState } from '../../shared/types/loading.type';
import { outputError } from '../../shared/utils/output-error.function';
import { MaintenanceClient } from '../../core/openapi/bff-admin-api/api/maintenance.client';
import { EquipmentClient } from '../../core/openapi/bff-admin-api/api/equipment.client';
import { SearchMaintenanceRequestsRequest } from '../../core/openapi/bff-admin-api/model/search-maintenance-requests-request';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { MaintenanceDictionaryService } from './maintenance-dictionary.service';
import { EquipmentBySerialNumberResponse } from '../../core/openapi/bff-admin-api/model/equipment-by-serial-number-response';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

@Injectable()
export class MaintenanceService {
  private readonly maintenanceClient = inject(MaintenanceClient);
  private readonly equipmentClient = inject(EquipmentClient);
  private readonly dictionaryService = inject(MaintenanceDictionaryService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly maintenanceReqParams$: BehaviorSubject<SearchMaintenanceRequestsRequest> =
    new BehaviorSubject<SearchMaintenanceRequestsRequest>({
      skip: 0,
      take: DEFAULT_TABLE_PAGE_SIZE,
      includeTotalCount: true,
    });

  private readonly _maintenanceTotalCount: WritableSignal<number> = signal(0);
  private readonly _maintenanceLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.LOADING,
  );

  public readonly statuses$: Observable<IDictionaryItem<number>[]> =
    this.dictionaryService.statuses$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          name: this.translateService.instant(`monitoring.maintenance.status.${item.code}`),
        })),
      ),
    );

  public readonly warrantyTypes$: Observable<IDictionaryItem<number>[]> =
    this.dictionaryService.warrantyTypes$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          name: this.translateService.instant(`monitoring.maintenance.warrantyType.${item.code}`),
        })),
      ),
    );

  public readonly maintenanceTypes$: Observable<IDictionaryItem<number>[]> =
    this.dictionaryService.maintenanceTypes$.pipe(
      map((items) =>
        items.map((item) => ({
          id: item.id,
          name: this.translateService.instant(
            `monitoring.maintenance.maintenanceType.${item.code}`,
          ),
        })),
      ),
    );

  public readonly statusesLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.statusesLoadingStatus;
  public readonly maintenanceTypesLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.maintenanceTypesLoadingStatus;
  public readonly warrantyTypesLoadingStatus: Signal<LoadingState> =
    this.dictionaryService.warrantyTypesLoadingStatus;

  private readonly getMaintenance$ = this.maintenanceReqParams$.pipe(
    tap(() => this._maintenanceLoadingStatus.set(LoadingState.LOADING)),
    switchMap((request) =>
      this.maintenanceClient.v1MaintenanceSearchPost(request).pipe(
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.maintenance.toast.maintenance-fetching-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });

          this._maintenanceTotalCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
        finalize(() => this._maintenanceLoadingStatus.set(LoadingState.READY)),
      ),
    ),
    map(({ items, totalCount }) => ({
      items: items ?? [],
      totalCount: totalCount ?? 0,
    })),
    tap(({ totalCount }) => {
      this._maintenanceTotalCount.set(totalCount ?? 0);
    }),
  );

  public readonly maintenance$ = this.getMaintenance$.pipe(map(({ items }) => items));

  public get maintenanceTotalCount(): Signal<number> {
    return this._maintenanceTotalCount.asReadonly();
  }

  public get maintenanceLoadingStatus(): Signal<LoadingState> {
    return this._maintenanceLoadingStatus.asReadonly();
  }

  public changeReqParams(params: Partial<SearchMaintenanceRequestsRequest>): void {
    this.maintenanceReqParams$.next({
      ...this.maintenanceReqParams$.value,
      ...params,
    });
  }

  public getEquipmentBySerialNumber(
    serialNumber: string,
  ): Observable<EquipmentBySerialNumberResponse | null> {
    return this.equipmentClient.v1EquipmentBySerialNumberSerialNumberGet(serialNumber).pipe(
      catchError((error) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.devices.toast.errors.equipment-fetching-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });
        }

        return of(null);
      }),
    );
  }
}
